import { useMemo } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Sparkles, Quote } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { Prefooter, Footer } from '@/components/layout'
import { Accordion, ProductCard, type AccordionItem } from '@/components/ui'
import {
  getProductBySlug,
  getRelatedProducts,
  lineLabels,
  type Product,
} from '@/data/products'

export function ProductDetailPage() {
  const { lang, slug } = useParams<{ lang: string; slug: string }>()
  const product = useMemo(() => (slug ? getProductBySlug(slug) : undefined), [slug])

  if (!product) {
    const fallback = lang === 'en' ? `/en/products/hair` : `/it/prodotti/capelli`
    return <Navigate to={fallback} replace />
  }

  return <ProductDetailView product={product} />
}

function ProductDetailView({ product }: { product: Product }) {
  const { t } = useTranslation('common')
  const detail = product.detail
  const related = getRelatedProducts(detail?.pairWith)
  const includedProducts = product.includes
    ?.map((s) => getProductBySlug(s))
    .filter((p): p is Product => Boolean(p)) ?? []

  const categoryValueMap: Record<string, string> = {
    capelli: t('productDetail.meta.categoryValueCapelli'),
    corpo: t('productDetail.meta.categoryValueCorpo'),
    face: t('productDetail.meta.categoryValueViso'),
    fragranze: t('productDetail.meta.categoryValueFragranze'),
    uomo: t('productDetail.meta.categoryValueUomo'),
    solari: t('productDetail.meta.categoryValueSolari'),
    novita: t('productDetail.meta.categoryValueNovita'),
  }

  const inciItems: AccordionItem[] = detail?.inci
    ? [
        {
          id: 'inci',
          question: t('productDetail.inci.accordionQuestion'),
          answer: (
            <div className="space-y-4">
              <p className="text-bruno-soft text-sm md:text-base leading-relaxed font-mono">
                {detail.inci}
              </p>
              <p className="text-bruno-muted text-xs md:text-sm italic leading-relaxed">
                {t('productDetail.inciDisclaimer')}
              </p>
            </div>
          ),
        },
      ]
    : []

  const faqItems: AccordionItem[] = (detail?.faq ?? []).map((f, i) => ({
    id: `faq-${i}`,
    question: f.q,
    answer: f.a,
  }))

  return (
    <main className="bg-cream">
      {/* Hero / single product */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-28 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-12 select-none font-display italic text-argan/[0.06]"
          style={{ fontSize: 'clamp(14rem, 24vw, 26rem)', lineHeight: 0.85 }}
        >
          n.
        </div>

        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            {/* Image stage */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
              className="lg:col-span-7 relative"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                {/* Editorial markings */}
                <div className="absolute top-6 left-6 z-10 flex items-center gap-3">
                  <span className="nashi-numeral text-bruno-muted">
                    {String(product.slug.length).padStart(2, '0')}.
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    {t('productDetail.hero.linePrefix')} {lineLabels[product.line]}
                  </span>
                </div>
                <div className="absolute bottom-6 right-6 z-10 text-right">
                  <p className="font-display italic text-argan-deep text-3xl">n.</p>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-bruno-muted mt-1">
                    {t('productDetail.hero.typeLabel')} · {product.format ?? t('productDetail.hero.typeFallback')}
                  </p>
                </div>

                {/* Soft radial glow */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_55%,rgba(207,169,128,0.18),transparent_65%)]"
                />

                <motion.img
                  src={product.image}
                  alt={product.name}
                  loading="eager"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.4, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
            </motion.div>

            {/* Info column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="lg:col-span-5 lg:pt-10"
            >
              <span className="nashi-eyebrow">{lineLabels[product.line]}</span>

              <h1
                className="mt-6 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2.5rem, 5vw, 4.25rem)',
                  lineHeight: 0.96,
                  letterSpacing: '-0.02em',
                }}
              >
                {product.name}
              </h1>

              {detail?.subtitle && (
                <p className="mt-4 font-display italic text-argan-deep text-xl md:text-2xl">
                  {detail.subtitle}
                </p>
              )}

              {detail?.description && (
                <p className="mt-8 text-bruno-soft text-base md:text-lg leading-relaxed">
                  {detail.description}
                </p>
              )}

              {!detail?.description && (
                <p className="mt-8 text-bruno-soft text-base leading-relaxed">
                  {t('productDetail.fallbackDescription')}
                </p>
              )}

              {/* Meta block */}
              <dl className="mt-10 grid grid-cols-2 gap-y-4 gap-x-6 pt-6 border-t border-bruno/10 text-[10px] uppercase tracking-[0.28em]">
                <div>
                  <dt className="text-bruno-muted">{t('productDetail.meta.formatLabel')}</dt>
                  <dd className="mt-1 text-bruno tabular-nums">
                    {product.format ?? t('productDetail.meta.formatFallback')}
                  </dd>
                </div>
                <div>
                  <dt className="text-bruno-muted">{t('productDetail.meta.lineLabel')}</dt>
                  <dd className="mt-1 text-bruno">{lineLabels[product.line]}</dd>
                </div>
                <div>
                  <dt className="text-bruno-muted">{t('productDetail.meta.categoryLabel')}</dt>
                  <dd className="mt-1 text-bruno">{categoryValueMap[product.category] ?? t('productDetail.meta.categoryValueCapelli')}</dd>
                </div>
                <div>
                  <dt className="text-bruno-muted">{t('productDetail.meta.availabilityLabel')}</dt>
                  <dd className="mt-1 text-bruno">{t('productDetail.meta.availabilityValue')}</dd>
                </div>
              </dl>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <LocalizedLink
                  to="storeLocator"
                  className="nashi-cta inline-flex items-center justify-center px-7 py-4 bg-bruno text-cream text-[11px] uppercase tracking-[0.2em] hover:bg-argan-deep transition-colors duration-500"
                >
                  {t('productDetail.cta.findSalon')}
                </LocalizedLink>
                <LocalizedLink
                  to="contacts"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 border border-bruno/30 text-bruno text-[11px] uppercase tracking-[0.2em] hover:border-argan hover:text-argan-deep transition-colors duration-500"
                >
                  {t('productDetail.cta.requestInfo')}
                </LocalizedLink>
              </div>

              {detail?.note && (
                <p className="mt-6 flex items-start gap-2 text-xs italic text-bruno-muted">
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 text-argan" strokeWidth={1.5} />
                  {detail.note}
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial #Nashilover */}
      {detail?.testimonial && (
        <section className="relative bg-cream-soft py-24 md:py-32 overflow-hidden">
          <div className="nashi-container">
            <div className="max-w-4xl mx-auto text-center relative">
              <Quote
                aria-hidden="true"
                className="mx-auto h-10 w-10 text-argan-deep mb-8"
                strokeWidth={1.2}
              />
              <motion.blockquote
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1 }}
                className="font-display italic text-bruno"
                style={{
                  fontSize: 'clamp(1.75rem, 3.2vw, 2.75rem)',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                «{detail.testimonial}»
              </motion.blockquote>
              <p className="mt-10 text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                {t('productDetail.testimonial.attribution')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Ideale per + Modalità d'uso */}
      {(detail?.idealFor || detail?.usage) && (
        <section className="relative bg-cream py-24 md:py-32 overflow-hidden">
          <div className="nashi-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-14">
              {detail?.idealFor && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1 }}
                >
                  <span className="nashi-eyebrow">{t('productDetail.idealFor.eyebrow')}</span>
                  <h2
                    className="mt-6 font-display text-bruno"
                    style={{
                      fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)',
                      lineHeight: 1.05,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    <span className="block">{t('productDetail.idealFor.headingLine1')}</span>
                    <span className="block italic font-light text-argan-deep">{t('productDetail.idealFor.headingLine2')}</span>
                  </h2>
                  <p className="mt-6 text-bruno-soft text-base md:text-lg leading-relaxed">
                    {detail.idealFor}
                  </p>
                </motion.div>
              )}

              {detail?.usage && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1, delay: 0.15 }}
                >
                  <span className="nashi-eyebrow">{t('productDetail.usage.eyebrow')}</span>
                  <h2
                    className="mt-6 font-display text-bruno"
                    style={{
                      fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)',
                      lineHeight: 1.05,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    <span className="block">{t('productDetail.usage.headingLine1')}</span>
                    <span className="block italic font-light text-argan-deep">
                      {t('productDetail.usage.headingLine2')}
                    </span>
                  </h2>
                  <p className="mt-6 text-bruno-soft text-base md:text-lg leading-relaxed">
                    {detail.usage}
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* INCI accordion */}
      {inciItems.length > 0 && (
        <section className="relative bg-cream-soft py-20 md:py-28 overflow-hidden border-y border-bruno/10">
          <div className="nashi-container max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-4">
                <span className="nashi-eyebrow">{t('productDetail.inci.eyebrow')}</span>
                <h2
                  className="mt-6 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
                    lineHeight: 1.05,
                    letterSpacing: '-0.01em',
                  }}
                >
                  <span className="block">{t('productDetail.inci.headingLine1')}</span>
                  <span className="block italic font-light text-argan-deep">{t('productDetail.inci.headingLine2')}</span>
                </h2>
                <p className="mt-5 text-bruno-soft text-sm leading-relaxed">
                  {t('productDetail.inci.description', { name: product.name })}
                </p>
              </div>
              <div className="lg:col-span-8">
                <Accordion items={inciItems} numbered={false} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqItems.length > 0 && (
        <section className="relative bg-cream py-24 md:py-32 overflow-hidden">
          <div className="nashi-container max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="lg:col-span-4">
                <span className="nashi-eyebrow">{t('productDetail.faq.eyebrow')}</span>
                <h2
                  className="mt-6 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(2rem, 3.4vw, 3rem)',
                    lineHeight: 1.02,
                    letterSpacing: '-0.02em',
                  }}
                >
                  <span className="block">{t('productDetail.faq.headingLine1')}</span>
                  <span className="block italic font-light text-argan-deep">{t('productDetail.faq.headingLine2')}</span>
                </h2>
              </div>
              <div className="lg:col-span-8">
                <Accordion items={faqItems} defaultOpen={0} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Included products (cofanetto) */}
      {includedProducts.length > 0 && (
        <section
          aria-label={t('productDetail.box.ariaLabel')}
          className="relative bg-cream py-24 md:py-32 overflow-hidden border-y border-bruno/10"
        >
          <div className="nashi-container">
            <div className="text-center mb-16 md:mb-20">
              <span className="nashi-eyebrow">{t('productDetail.box.eyebrow')}</span>
              <h2
                className="mt-5 font-display text-bruno"
                style={{
                  fontSize: 'clamp(2rem, 3.6vw, 3.25rem)',
                  lineHeight: 1,
                  letterSpacing: '-0.01em',
                }}
              >
                <span className="block">{t('productDetail.box.headingLine1')}</span>
                <span className="italic font-light text-argan-deep">{t('productDetail.box.headingLine2')}</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {includedProducts.map((ip, idx) => (
                <motion.div
                  key={ip.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.8, delay: idx * 0.15 }}
                  className="group text-center"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-cream-soft mb-6">
                    <img
                      src={ip.image}
                      alt={ip.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="font-display text-bruno text-xl md:text-2xl">
                    {ip.name}
                  </h3>
                  {ip.detail?.subtitle && (
                    <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-bruno-muted">
                      {ip.detail.subtitle}
                    </p>
                  )}
                  {ip.detail?.description && (
                    <p className="mt-4 text-bruno-soft text-sm leading-relaxed max-w-xs mx-auto line-clamp-4">
                      {ip.detail.description}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related products */}
      {related.length > 0 && (
        <section
          aria-label={t('productDetail.related.ariaLabel')}
          className="relative bg-cream-soft py-24 md:py-32 overflow-hidden"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-32 left-[-3%] select-none font-display italic text-argan/[0.05]"
            style={{ fontSize: 'clamp(14rem, 28vw, 28rem)', lineHeight: 0.85 }}
          >
            &
          </div>

          <div className="relative nashi-container">
            <div className="flex items-end justify-between mb-12 md:mb-16 pb-6 border-b border-bruno/10">
              <div>
                <span className="nashi-eyebrow">{t('productDetail.related.eyebrow')}</span>
                <h2
                  className="mt-4 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(2rem, 3.6vw, 3.25rem)',
                    lineHeight: 1,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {t('productDetail.related.heading')}{' '}
                  <span className="italic font-light text-argan-deep">{t('productDetail.related.headingItalic')}</span>
                </h2>
              </div>
              <LocalizedLink
                to="productsHair"
                className="hidden md:inline text-[11px] uppercase tracking-[0.2em] text-bruno hover:text-argan-deep transition-colors"
              >
                {t('productDetail.related.viewAll')}
              </LocalizedLink>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-12">
              {related.map((rp, idx) => (
                <ProductCard key={rp.slug} product={rp} index={idx} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Prefooter />
      <Footer />
    </main>
  )
}
